import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  TableContainer,
  Icon,
  VStack,
  useBreakpointValue,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FiSearch, FiMonitor, FiArrowLeft } from "react-icons/fi";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";
import Pagination from "../../Components/Common/Pagination";
import axios from "axios";
import { api_url } from "../../url";

const SIDEBAR_WIDTH = "240px";

const AllWithdrawals = () => {
  const [searchTrx, setSearchTrx] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();

  const fetchAllWithdrawals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${api_url}/withdrawals/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit, search: searchTrx },
      });
      if (res.data.success) {
        setWithdrawals(res.data.withdrawals);
        setTotalPages(res.data.pagination.pages);
        setTotalEntries(res.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching withdrawals", error);
      toast({ title: "Error fetching withdrawals", status: "error", duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllWithdrawals();
  }, [page, limit, searchTrx]);

  const handleShowDetail = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleBack = () => {
    setSelectedWithdrawal(null);
  };

  const filteredWithdrawals = withdrawals;

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        {loading && withdrawals.length === 0 ? (
          <Flex justify="center" align="center" h="60vh">
            <Spinner size="xl" color="#4c3cff" />
          </Flex>
        ) : selectedWithdrawal ? (
          <Box maxW="1200px" mx="auto">
            {/* Detail Header */}
            <Flex
              align="center"
              mb={6}
              justify="space-between"
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="700"
                color="#344767"
              >
                Withdrawal Details
              </Text>
              <Button
                leftIcon={<FiArrowLeft />}
                variant="ghost"
                color="#5e72e4"
                onClick={handleBack}
                _hover={{ bg: "blue.50" }}
              >
                Back to List
              </Button>
            </Flex>

            <Flex direction={{ base: "column", lg: "row" }} gap={6}>
              {/* Left card: Withdrawal Details */}
              <Box
                flex="1"
                bg="white"
                borderRadius="12px"
                p={8}
                boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.07), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
              >
                <Text
                  fontSize="xl"
                  fontWeight="600"
                  color="#67748e"
                  mb={8}
                  opacity="0.8"
                >
                  Withdraw Via {selectedWithdrawal.bankDetails?.bankName}
                </Text>

                <Table variant="simple" size="sm">
                  <Tbody>
                    <Tr borderBottom="1px" borderColor="gray.100">
                      <Td py={5} color="#67748e" fontSize="md">
                        Date
                      </Td>
                      <Td
                        py={5}
                        textAlign="right"
                        fontWeight="700"
                        color="#344767"
                        fontSize="md"
                      >
                        {new Date(selectedWithdrawal.createdAt).toLocaleString()}
                      </Td>
                    </Tr>
                    <Tr borderBottom="1px" borderColor="gray.100">
                      <Td py={5} color="#67748e" fontSize="md">
                        Trx Number
                      </Td>
                      <Td
                        py={5}
                        textAlign="right"
                        fontWeight="700"
                        color="#344767"
                        fontSize="md"
                      >
                        {selectedWithdrawal.transactionId}
                      </Td>
                    </Tr>
                    <Tr borderBottom="1px" borderColor="gray.100">
                      <Td py={5} color="#67748e" fontSize="md">
                        Username
                      </Td>
                      <Td
                        py={5}
                        textAlign="right"
                        fontWeight="700"
                        color="#007bff"
                        fontSize="md"
                      >
                        @{selectedWithdrawal.user?.username}
                      </Td>
                    </Tr>
                    <Tr borderBottom="1px" borderColor="gray.100">
                      <Td py={5} color="#67748e" fontSize="md">
                        Amount
                      </Td>
                      <Td
                        py={5}
                        textAlign="right"
                        fontWeight="700"
                        color="#344767"
                        fontSize="md"
                      >
                        ₹{selectedWithdrawal.amount.toFixed(2)}
                      </Td>
                    </Tr>
                    <Tr borderBottom="1px" borderColor="gray.100">
                      <Td py={5} color="#67748e" fontSize="md">
                        Status
                      </Td>
                      <Td py={5} textAlign="right">
                        <Badge
                          colorScheme={
                            selectedWithdrawal.status === "approved" ? "green" :
                            selectedWithdrawal.status === "pending" ? "orange" : "red"
                          }
                          variant="solid"
                          borderRadius="20px"
                          px={3}
                          py={1}
                        >
                          {selectedWithdrawal.status.toUpperCase()}
                        </Badge>
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>

                {selectedWithdrawal.remark && (
                  <Box
                    mt={6}
                    p={4}
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="gray.100"
                    bg="gray.50"
                  >
                    <Text color="#344767" fontWeight="700" mb={1}>
                      Admin Remark
                    </Text>
                    <Text color="#67748e" fontSize="sm">
                      {selectedWithdrawal.remark}
                    </Text>
                  </Box>
                )}
              </Box>

              {/* Right card: User Withdraw Information */}
              <Box
                flex="1.2"
                bg="white"
                borderRadius="12px"
                p={8}
                boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.07), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
                h="fit-content"
              >
                <Text
                  fontSize="lg"
                  fontWeight="600"
                  color="#344767"
                  mb={4}
                  pb={4}
                  borderBottom="1px"
                  borderColor="gray.100"
                >
                  Bank Account Information
                </Text>

                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text color="#344767" fontWeight="700" mb={1}>
                      Bank Name
                    </Text>
                    <Text color="#67748e">{selectedWithdrawal.bankDetails?.bankName}</Text>
                  </Box>
                  <Box>
                    <Text color="#344767" fontWeight="700" mb={1}>
                      Account Number
                    </Text>
                    <Text color="#67748e" fontWeight="bold" fontSize="lg">{selectedWithdrawal.bankDetails?.accountNumber}</Text>
                  </Box>
                  <Box>
                    <Text color="#344767" fontWeight="700" mb={1}>
                      IFSC Code
                    </Text>
                    <Text color="#67748e">{selectedWithdrawal.bankDetails?.ifscCode}</Text>
                  </Box>
                  <Box>
                    <Text color="#344767" fontWeight="700" mb={1}>
                      Mobile Number
                    </Text>
                    <Text color="#67748e">{selectedWithdrawal.user?.mobile || "N/A"}</Text>
                  </Box>
                </VStack>
              </Box>
            </Flex>
          </Box>
        ) : (
          <>
            <Flex
              justify="space-between"
              align="center"
              mb={8}
              direction={{ base: "column", lg: "row" }}
              gap={4}
            >
              <Text fontSize="2xl" fontWeight="700" color="#344767">
                All Withdrawals
              </Text>
              <Flex
                gap={4}
                direction={{ base: "column", md: "row" }}
                w={{ base: "100%", lg: "auto" }}
              >
                <InputGroup size="md" maxW={{ md: "300px" }}>
                  <Input
                    placeholder="Username / TRX"
                    value={searchTrx}
                    onChange={(e) => setSearchTrx(e.target.value)}
                    bg="white"
                    borderRadius="8px"
                  />
                  <InputRightElement w="3rem">
                    <Button
                      h="100%"
                      w="100%"
                      bg="#4c3cff"
                      color="white"
                      borderRadius="0 8px 8px 0"
                    >
                      <Icon as={FiSearch} />
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </Flex>
            </Flex>

            {/* MOBILE CARD VIEW */}
            {isMobile ? (
              <VStack spacing={4} align="stretch">
                {filteredWithdrawals.map((w, i) => (
                  <Box
                    key={i}
                    bg="white"
                    borderRadius="12px"
                    p={4}
                    boxShadow="0 2px 4px rgba(0, 0, 0, 0.05)"
                    border="1px solid #f0f2f5"
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Text fontSize="sm" fontWeight="700" color="#32325d">
                        Bank
                      </Text>
                      <VStack align="flex-end" spacing={0}>
                        <Text fontSize="md" fontWeight="700" color="#5e72e4">
                          {w.bankDetails?.bankName}
                        </Text>
                        <Text fontSize="xs" color="#67748e">
                          {w.transactionId}
                        </Text>
                      </VStack>
                    </Flex>

                    <Flex
                      justify="space-between"
                      py={2}
                      borderTop="1px solid #f0f2f5"
                    >
                      <Text fontSize="sm" fontWeight="700" color="#32325d">
                        Amount
                      </Text>
                      <Text fontSize="md" color="#344767" fontWeight="bold">
                        ₹{w.amount.toFixed(2)}
                      </Text>
                    </Flex>

                    <Flex
                      justify="space-between"
                      py={2}
                      borderTop="1px solid #f0f2f5"
                    >
                      <Text fontSize="sm" fontWeight="700" color="#32325d">
                        Status
                      </Text>
                      <Badge
                        colorScheme={w.status === "approved" ? "green" : w.status === "pending" ? "orange" : "red"}
                        variant="solid"
                        borderRadius="20px"
                        px={3}
                        py={1}
                      >
                        {w.status.toUpperCase()}
                      </Badge>
                    </Flex>

                    <Flex
                      justify="space-between"
                      pt={2}
                      borderTop="1px solid #f0f2f5"
                    >
                      <Text fontSize="sm" fontWeight="700" color="#32325d">
                        Action
                      </Text>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        size="sm"
                        leftIcon={<Icon as={FiMonitor} />}
                        onClick={() => handleShowDetail(w)}
                        borderRadius="8px"
                        fontSize="xs"
                        fontWeight="600"
                        px={4}
                      >
                        Details
                      </Button>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            ) : (
              /* DESKTOP TABLE VIEW */
              <Box
                bg="white"
                borderRadius="12px"
                boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                overflow="hidden"
                mb={0}
              >
                <TableContainer>
                  <Table variant="simple">
                    <Thead bg="#4c3cff">
                      <Tr>
                        <Th color="white" textTransform="none" fontSize="sm" py={5}>
                          Bank | Transaction
                        </Th>
                        <Th color="white" textTransform="none" fontSize="sm" py={5}>
                          Initiated
                        </Th>
                        <Th color="white" textTransform="none" fontSize="sm" py={5}>
                          User
                        </Th>
                        <Th color="white" textTransform="none" fontSize="sm" py={5}>
                          Amount
                        </Th>
                        <Th color="white" textTransform="none" fontSize="sm" py={5}>
                          Status
                        </Th>
                        <Th color="white" textTransform="none" fontSize="sm" py={5}>
                          Action
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredWithdrawals.map((w, i) => (
                        <Tr key={i} _hover={{ bg: "#f8f9fa" }}>
                          <Td py={4}>
                            <Text fontWeight="700" color="#5e72e4" fontSize="sm">
                              {w.bankDetails?.bankName}
                            </Text>
                            <Text color="#67748e" fontSize="xs">
                              {w.transactionId}
                            </Text>
                          </Td>
                          <Td>
                            <Text color="#344767" fontWeight="600" fontSize="sm">
                              {new Date(w.createdAt).toLocaleString()}
                            </Text>
                          </Td>
                          <Td>
                            <Text color="#344767" fontWeight="700" fontSize="sm">
                              {w.user?.username}
                            </Text>
                          </Td>
                          <Td>
                            <Text color="#344767" fontWeight="bold">
                              ₹{w.amount.toFixed(2)}
                            </Text>
                          </Td>
                          <Td>
                             <Badge
                                colorScheme={w.status === "approved" ? "green" : w.status === "pending" ? "orange" : "red"}
                                variant="solid"
                                borderRadius="20px"
                                px={3}
                                py={1}
                              >
                                {w.status.toUpperCase()}
                              </Badge>
                          </Td>
                          <Td>
                            <Button
                              variant="outline"
                              colorScheme="blue"
                              size="sm"
                              leftIcon={<Icon as={FiMonitor} />}
                              onClick={() => handleShowDetail(w)}
                            >
                              Details
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                      {filteredWithdrawals.length === 0 && (
                        <Tr>
                          <Td colSpan={6} textAlign="center">No withdrawals found</Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Pagination */}
            {!selectedWithdrawal && (
              <Pagination 
                currentPage={page}
                totalPages={totalPages}
                totalEntries={totalEntries}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={handleLimitChange}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default AllWithdrawals;
